import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import type { UserRole } from '../types';
import { adminSignInPath, customerSignInPath } from './experience';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Which experience this gate belongs to — drives login destination and role isolation. */
  experience: 'customer' | 'admin';
  roles?: UserRole[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  experience,
  roles,
}) => {
  const { isAuthenticated, isLoading, hasRole, isAdmin } = useAuth();
  const location = useLocation();
  const next = `${location.pathname}${location.search}`;

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-xs text-fog" role="status">
        …
      </div>
    );
  }

  if (!isAuthenticated) {
    const to =
      experience === 'admin' ? adminSignInPath(next) : customerSignInPath(next);
    return <Navigate to={to} replace state={{ from: next }} />;
  }

  if (experience === 'customer' && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (experience === 'admin' && !isAdmin) {
    return (
      <Navigate
        to={adminSignInPath(next)}
        replace
        state={{ from: next, reason: 'forbidden' }}
      />
    );
  }

  if (roles && roles.length > 0) {
    const allowed = roles.some((r) => hasRole(r));
    if (!allowed) {
      const to =
        experience === 'admin'
          ? adminSignInPath(next)
          : customerSignInPath(next);
      return <Navigate to={to} replace state={{ from: next, reason: 'forbidden' }} />;
    }
  }

  return <>{children}</>;
};
